/**
 * #947: yöneticinin Amazon P&L sayfasında seçtiği satıcıyı SALT-OKUR görmesi (`?viewAs=<bağlantı>`).
 *
 * İki parça, tek kaynak (sayfa kapısı `AmazonViewAsGate`):
 *  - React bağlamı: sorgu anahtarları satıcıya göre ayrışır (yönetici verisi ile satıcı verisi
 *    önbellekte KARIŞMAZ) ve bileşenler yazma kontrollerini gizler (`readOnly`).
 *  - Modül değişkeni: `apiFetch` yalnız sunucuda `@view_as_aware` olan Amazon sayfası GET uçlarına
 *    (sayfa uçları; liste `AWARE_PREFIXES`) `viewAs` ekler. Yazma
 *    yöntemlerine ASLA eklenmez; sunucu zaten `viewAs`'lı yazmayı 403 ile reddeder.
 * Yönetici olmayanda kapı değeri hiç kurmaz: ekran ve istekler bugünküyle birebir aynıdır.
 */
import { createContext, useContext } from 'react'

/** `?viewAs=` yalnız bağlantı uuid'i kabul eder; başka biçim yok sayılır (#883 ile aynı kural). */
export const VIEW_AS_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

let currentViewAs: string | null = null

export function getAmazonViewAs(): string | null {
  return currentViewAs
}

/** Kapı, her çizimde ve efektte (idempotent) çağırır; `apiFetch` bundan okur. */
export function setAmazonViewAs(id: string | null): void {
  currentViewAs = id
}

/** Sunucuda `@view_as_aware` olan uçlar (backend kapsam testindeki READ_ROUTES ile aynı küme). */
const AWARE_PREFIXES = [
  '/amazon/connections',
  '/amazon/custom-items',
  '/amazon/dashboard/freshness',
  '/amazon/dashboard/backfill-status',
  '/amazon/dashboard/pl-summary',
  '/amazon/dashboard/pl-trend',
  '/amazon/dashboard/pl-products',
  '/amazon/dashboard/pl-product-detail',
  '/amazon/dashboard/pl-order-items',
  '/amazon/dashboard/cogs',
  '/amazon/dashboard/orders',
  '/amazon/dashboard/returns',
  '/amazon/dashboard/settlement',
  '/amazon/dashboard/fees',
  // #1152: Reklam sayfasının salt-okur GET uçları (yazma uçları `/ads/sync/trigger`, `/ads/target-acos` YOK).
  '/amazon/ads/campaigns',
  '/amazon/ads/search-terms',
  '/amazon/ads/tacos',
  '/amazon/ads/cross-keyword',
]

/** Sayfa GET yoluna `viewAs` ekler (görünüm yoksa ya da yol kapsam dışıysa aynen döndürür). */
export function withAmazonViewAs(path: string, method?: string): string {
  if (!currentViewAs) return path
  if (method && method.toUpperCase() !== 'GET') return path
  const normalized = path.startsWith('/') ? path : `/${path}`
  if (!AWARE_PREFIXES.some((prefix) => normalized.startsWith(prefix))) return path
  if (/[?&]viewAs=/.test(normalized)) return path
  return `${normalized}${normalized.includes('?') ? '&' : '?'}viewAs=${encodeURIComponent(currentViewAs)}`
}

export const AmazonViewAsContext = createContext<string | null>(null)

/** Seçili satıcı bağlantı kimliği; görünüm yoksa `null`. */
export function useAmazonViewAs(): string | null {
  return useContext(AmazonViewAsContext)
}

/** Salt-okur görünümde yazma kontrolleri çizilmez / tıklanamaz. */
export function useAmazonReadOnly(): boolean {
  return useContext(AmazonViewAsContext) !== null
}
